import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { API_BASE_URL } from '../../config';

export interface UndoPlan {
  batchId: string;
  moveEventId: number;
  movedAt: string | null;
  movedBy: string | null;
  volumeBbl: number | null;
  /** Present when the voided move converted/relocated stage WIP inventory. */
  wipConvert?: { kind?: string } | null;
  current: { equipmentId: number | null; stage: string | null };
  previous: { equipmentId: number | null; name: string | null; abbreviation: string | null; stage: string | null };
  needsVessel: boolean;
}

interface Props {
  batchId: string;
  /** Vessel names for the current/previous ids and the pick-a-vessel fallback. */
  equipment: { equipmentId: number; name: string; abbreviation?: string | null }[];
  onCancel: () => void;
  onDone: (message: string, result: { equipmentId: number | null; stage: string | null }) => void;
}

const fmtTime = (iso: string | null) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' }) + ' CT';
  } catch {
    return iso;
  }
};

/** "Undo last move": preview (GET) then POST /api/batches/:id/undo-move. */
const UndoMoveModal: React.FC<Props> = ({ batchId, equipment, onCancel, onDone }) => {
  const [plan, setPlan] = useState<UndoPlan | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/batches/${encodeURIComponent(batchId)}/undo-move`, { headers: { Accept: 'application/json' } });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 404) setBlocked(data.blockedReason || data.error || 'No recorded move to undo for this batch');
        else if (!res.ok) setBlocked(data.error || `HTTP ${res.status}`);
        else {
          setPlan(data.plan || null);
          if (!data.ok && !data.plan?.needsVessel) setBlocked(data.blockedReason);
        }
      } catch (err: any) {
        if (!cancelled) setBlocked('Could not load the last move: ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  const name = (id: number | null | undefined) => {
    const eq = equipment.find((e) => Number(e.equipmentId) === Number(id));
    return eq ? eq.abbreviation || eq.name : id != null ? `#${id}` : 'no vessel';
  };
  const prevLabel = plan?.previous.equipmentId != null ? plan.previous.abbreviation || plan.previous.name || name(plan.previous.equipmentId) : pick ? name(Number(pick)) : '…';

  const confirm = async () => {
    if (plan?.needsVessel && !pick) return setError('Pick the vessel to move back to.');
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${encodeURIComponent(batchId)}/undo-move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(plan?.needsVessel ? { equipmentId: Number(pick) } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onDone(data.message || `Move undone for ${batchId}`, { equipmentId: data.equipmentId ?? null, stage: data.stage ?? null });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="facility-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="undo-title" data-testid="undo-move-modal">
      <div className="facility-modal">
        <h3 id="undo-title">Undo last move · {batchId}</h3>
        {loading && <p className="facility-modal__meta">Loading last move…</p>}
        {plan && (
          <p className="facility-modal__meta" data-testid="undo-summary">
            Last move: <strong>{name(plan.current.equipmentId)}</strong>
            {plan.volumeBbl != null ? ` (${plan.volumeBbl} bbl transferred)` : ''}
            {plan.movedAt ? ` on ${fmtTime(plan.movedAt)}` : ''}
            {plan.movedBy ? ` by ${plan.movedBy}` : ''}.
            <br />
            Undo moves it back to <strong>{prevLabel}</strong> at <strong>{plan.previous.stage}</strong>
            {plan.volumeBbl != null ? ` and voids the ${plan.volumeBbl} bbl transfer` : ''}
            {plan.wipConvert ? ', and reverses the Wort→Brite WIP inventory convert' : ''}. No TTB records change.
          </p>
        )}
        {plan?.needsVessel && !blocked && (
          <>
            <label htmlFor="undo-vessel">The previous vessel wasn't recorded. Move back to:</label>
            <select id="undo-vessel" data-testid="undo-vessel" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Select vessel</option>
              {equipment
                .filter((e) => Number(e.equipmentId) !== Number(plan.current.equipmentId))
                .map((e) => (
                  <option key={e.equipmentId} value={e.equipmentId}>
                    {e.abbreviation ? `${e.abbreviation} · ${e.name}` : e.name}
                  </option>
                ))}
            </select>
          </>
        )}
        {blocked && (
          <div className="facility-modal__error" role="alert" data-testid="undo-blocked">
            {blocked}
          </div>
        )}
        {error && (
          <div className="facility-modal__error" role="alert" data-testid="undo-error">
            {error}
          </div>
        )}
        <div className="facility-modal__actions">
          <button type="button" className="is-primary" data-testid="undo-confirm" onClick={confirm} disabled={busy || loading || !!blocked || !plan}>
            {busy ? 'Undoing…' : 'Undo move'}
          </button>
          <button type="button" className="is-secondary" data-testid="undo-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default UndoMoveModal;
